import React, { MouseEventHandler, useEffect, useRef, useState } from "react";
import CareersHeader from "../Shared/CareersHeader";
import CareersImageGrid from "../Shared/CareersImageGrid";
import CareersIntroduction from "../Shared/CareersIntroduction";
import CareersQuote from "../Shared/CareersQuote";
import CareersTeam from "../Shared/CareersTeam";
import CareersVideo from "../Shared/CareersVideo";
import { SingleDepartmentJobBrowser } from "../Shared/JobListings";
import Department from "../Shared/JobListings/Department";
import JobListing from "../Shared/JobListings/JobListing";
import CareersFooter from "../Shared/CareersFooter";
import AlumniSchool from "../Alumni/Models/AlumniSchool";
import AlumniSection from "../Shared/AlumniSection";
import DepartmentDetailContent from "../Shared/JobListings/DepartmentDetailContent";
import QueryStringParameterSearch from "~/common/QueryString/QueryStringParameterSearch";

interface Props {
  displayHeader: string;
  jobListings: JobListing[];
  department: Department;
  departmentDetail: DepartmentDetailContent;
  collegeSchools: AlumniSchool[];
}

export default function CareersDepartment({
  displayHeader,
  jobListings,
  department,
  departmentDetail,
  collegeSchools,
}: Props) {
  const careersVideoRef = useRef<HTMLDivElement>(null);
  const jobListingsRef = useRef<HTMLDivElement>(null);
  const [
    initiallySelectedState,
    setInitiallySelectedState,
  ] = useState<string>();

  const onClickAboutUs: MouseEventHandler = (event) => {
    event.preventDefault();

    if (!careersVideoRef.current) {
      return;
    }

    careersVideoRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  };

  const onClickViewJobs: MouseEventHandler = (event) => {
    event.preventDefault();

    if (!jobListingsRef.current) {
      return;
    }

    jobListingsRef.current.scrollIntoView({
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const locationValue = QueryStringParameterSearch("location");
    if (!locationValue) {
      return;
    }

    const locationHasListings =
      jobListings.find(
        (listing) =>
          listing.stateName.toUpperCase() === locationValue?.toUpperCase()
      ) !== undefined;

    if (locationHasListings) {
      setInitiallySelectedState(locationValue);
    } else {
      window.history.replaceState(
        null,
        window.document.title,
        window.location.pathname
      );
    }
  }, [jobListings]);

  return (
    <div
      data-module="careersDepartment"
      className="careers-container department-view"
    >
      <CareersHeader
        title={displayHeader}
        className={department ? department.seoUrlKey : "department-landing"}
        subTitle={departmentDetail?.header?.subTitle}
        topRightButton={
          <button
            type="button"
            className="btn-outline header-btn about-us"
            onClick={onClickAboutUs}
          >
            About Us
          </button>
        }
        callToAction={
          <button
            type="button"
            className="btn-fill view-positions"
            onClick={onClickViewJobs}
          >
            View All Jobs
          </button>
        }
        careersLinkTarget="_self"
      />
      <SingleDepartmentJobBrowser
        ref={jobListingsRef}
        department={department}
        jobListings={jobListings}
        className="subpage"
        topRightButton={
          <a href="/careers/#careers-positions" className="btn-outline-reverse">
            <i className="icon-left-open" /> View All Jobs
          </a>
        }
        initialStateSelected={initiallySelectedState}
      />

      <CareersIntroduction
        title={departmentDetail?.introduction?.title}
        subTitle={departmentDetail?.introduction?.subTitle}
        text={departmentDetail?.introduction?.text}
      />
      <CareersVideo ref={careersVideoRef} />
      <CareersTeam
        subTitle={departmentDetail?.team?.subTitle}
        perks={departmentDetail?.team?.perks}
      />
      <CareersImageGrid
        images={departmentDetail?.imageGrid?.images?.map((image) => ({
          src: image,
        }))}
        overlay={
          <img
            src={
              departmentDetail?.imageGrid?.overlayImageSrc ??
              "//cdnimg.webstaurantstore.com/uploads/design/2017/5/lifeatweb.png"
            }
            alt={
              departmentDetail?.imageGrid?.overlayImageAlt ??
              "Life At WebstaurantStore"
            }
          />
        }
      />
      <CareersQuote
        imageSrc={departmentDetail?.quote?.imageSrc}
        title={departmentDetail?.quote?.title}
        quote={departmentDetail?.quote?.quote}
        author={departmentDetail?.quote?.author}
      />
      {!department && <AlumniSection collegeSchools={collegeSchools} />}
      <CareersFooter />
    </div>
  );
}
